import { handleIntents } from '@/embed/intents';
import setupConditionalWidgets from '@/embed/conditional_widget';
import setupSupplierLists, {
  EVENT_SUPPLIER_LIST_UPDATE
} from '@/embed/supplier_list';
import setupTariffLists, {
  EVENT_TARIFF_LIST_UPDATE
} from '@/embed/tariff_list';
import setupSubmissionData from '@/embed/submission_data';
import setupFormValidation from '@/embed/form_validation';
import setupAddressHistories, {
  EVENT_ADDRESS_ADDED
} from '@/embed/address_history';
import APIClient from '@/embed/api';
import {
  send,
  withPreviousRequestCancellation,
  addQueryParams
} from '@/lib/xhr';
import { delegate, hasModifiers } from '@/lib/events';
import { isHashLinkOnSamePage, isEmptyHashLink } from '@/lib/links';
import { renderGlobalError } from '@/lib/simpleFormErrors';
import withPolyfill from '@/lib/polyfill';
import setupAddresses from '@/embed/address';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import setupWithJS from '@/embed/with_js';
import { setupIsFilled } from '@/embed/is_filled';
import setupSmartMeterOptions from '@/embed/smart_meter_options';
import { setupClearBeforeShow } from '@/embed/clear_before_show';
import setupPrefillFromAddress from '@/embed/pre_fill_from_address';
import setupFormGroupTooltips from '@/embed/form_group_tooltip';
// Because easy-toggle-state initializes CustomEvents out of its main function
// need to load polyfill for everyone so IE10/11 get it
import '@/lib/custom_event_polyfill';
import setupEasyToggleState from 'easy-toggle-state/src/main/main';
import setupGasAndElectricityLabels from '@/embed/gas_and_electricity_label';
import setupBreadcrumbs from '@/embed/breadcrumb';
import 'inert-polyfill';
import setupEnergyUsages from '@/embed/energy_usage';
import setupAnalytics, { sendSlideAnalytics } from '@/embed/analytics';
import {
  setupDisableWhileLoading,
  CLASS_PREVENT_SUBMISSION
} from '@/embed/disable_while_loading';
import setupSwitchReminders from '@/embed/switch_reminder';
import setupMpanMprnFields from '@/embed/mpan_mprn_fields';
import setupPayMethodSync from '@/embed/pay_method_sync';
import setupSharingLinks from '@/embed/sharing_link';
import { setupNativeSharing } from '@/embed/sharing_native';
import setupClipboard from '@/embed/clipboard';
import setupSelectOnClick from '@/embed/select_on_click';

const sendWithCancellation = withPreviousRequestCancellation(send);

function submit(form, submitEl) {
  const data = new FormData(form);
  if (submitEl) {
    data.append(submitEl.name, submitEl.value);
  }

  return sendWithCancellation(form.action, {
    method: form.method,
    responseType: 'json',
    withCredentials: true,
    headers: {
      Accept: 'application/json'
    },
    data
  }).then(e => e.target.parsedResponse);
}

function visit(url, { data = {} }) {
  return sendWithCancellation(addQueryParams(url, data), {
    responseType: 'json',
    withCredentials: true,
    headers: {
      Accept: 'application/json'
    }
  }).then(e => e.target.parsedResponse);
}

const CLASS_ANCHOR = 'bcs-slide__anchor';

const SELECTOR = '.bcs-slide';
const SELECTOR_SLIDE = '.js-bcs-slide-content';
const SELECTOR_ANALYTICS = '.js-bcs-analytics';
// An invisible anchor to control spacing when scrolling back up
const SELECTOR_ANCHOR = `.${CLASS_ANCHOR}`;
const ATTR_BASE_URL = 'data-bcs-api-url';

const CLASS_BUSY = 'is-busy';

export const ERROR_MESSAGE = `
  We're sorry, something's not working.
  Please call us on <a href="tel:+448002494770">0800 249 4770</a>,
  email us at <a href="mailto:help@bigcleanswitch.org">help@bigcleanswitch.org</a>
  or message us through our live chat where available and we'll get to the bottom of it as quickly as we can.
  We're sorry that your experience hasn't been what we would have hoped and we're equally determined to get it fixed.
`;

// Analytics are activated for all browsers
setupAnalytics();

// Little mustard cutting so we can provide light JS to browsers
if (window.history) {
  withPolyfill(error => {
    if (error) {
      return;
    }

    // Track which submit button submits the form so their
    // values can be included in the form data
    let submitEl;
    document.addEventListener(
      'click',
      delegate("[type='submit']", (e, target) => {
        submitEl = target;
      })
    );

    function withSubmitEl(fn) {
      return e => {
        fn(e, submitEl);
        submitEl = null;
      };
    }

    // Ensure history.state is empty after a page reload
    window.history.replaceState(null, document.title, document.location);

    setupClipboard();
    setupEnergyUsages();
    setupPayMethodSync();
    setupNativeSharing();
    setupSelectOnClick();
    setupDisableWhileLoading([
      EVENT_TARIFF_LIST_UPDATE,
      EVENT_SUPPLIER_LIST_UPDATE
    ]);

    Array.from(document.querySelectorAll(SELECTOR)).forEach((el, index) => {
      const client = new APIClient(el.getAttribute(ATTR_BASE_URL));
      // As the takeover will change where the slides sit in the DOM
      // we need to reference which container they're in
      let slideContainer;
      let validator;

      const withinIframe = isWithinIframe(el);

      // Sets up the various JavaScript behaviours on the slide
      function setup(scope = slideContainer) {
        maybeUpdateSlideContainer(scope);
        setupClearBeforeShow(scope);
        setupWithJS(scope);
        setupIsFilled(scope);
        setupGasAndElectricityLabels(scope);
        setupSubmissionData(scope);
        setupConditionalWidgets(scope);
        setupAddressHistories(scope, client);
        setupSmartMeterOptions(scope);
        setupPrefillFromAddress(scope);
        setupMpanMprnFields(scope);
        setupFormGroupTooltips(scope);
        setupBreadcrumbs(scope);
        setupSharingLinks(scope);
        setupEasyToggleState();
        validator = setupFormValidation(scope);
        setupSupplierLists(scope, client, validator);
        setupTariffLists(scope, client, validator);
        setupAddresses(scope, client, validator);
        setupSwitchReminders(scope, client, validator);
        sendSlideAnalytics(scope.querySelector(SELECTOR_ANALYTICS));
      }

      function maybeUpdateSlideContainer(scope) {
        if (slideContainer && slideContainer !== scope) {
          slideContainer.removeEventListener('submit', handleFormSubmission);
          slideContainer.removeEventListener('click', handleLinkNavigation);
          slideContainer.removeEventListener(
            EVENT_ADDRESS_ADDED,
            handleAddressAdded
          );
        }

        slideContainer = scope;
        slideContainer.addEventListener('click', handleLinkNavigation);
        slideContainer.addEventListener('submit', handleFormSubmission);
        slideContainer.addEventListener(
          EVENT_ADDRESS_ADDED,
          handleAddressAdded
        );
        addAnchor(slideContainer);
      }

      function scrollToStart() {
        scrollIntoViewIfNeeded(
          slideContainer.querySelector(SELECTOR_ANCHOR) || slideContainer,
          {
            block: 'start',
            scrollMode: 'if-needed'
          }
        );
        const event = new CustomEvent('didScrollToTop', { bubbles: true });
        document.dispatchEvent(event);
      }

      maybeUpdateSlideContainer(el);
      setup();

      window.addEventListener('popstate', handlePopState);
      function handlePopState(event) {
        // Make sure only the embedded slide that had triggered the pushState
        // handles the popstate
        if (index == event.state.slideIndex) {
          // Trigger a full reload on the initial slide
          // so that data from WP is re-set appropriately
          if (event.state.initialSlide) {
            window.location.reload();
          } else {
            navigate(
              () =>
                visit(event.state.slideUrl, {
                  data: withinIframe ? { layout_type: 'iframe' } : null
                }),
              { pushState: false }
            );
          }
        }
      }

      function handleAddressAdded({ detail }) {
        const { address } = detail;
        validator.addElements(address);
      }

      function handleFormSubmission(event) {
        return withSubmitEl((event, submitEl) => {
          if (!event.defaultPrevented) {
            event.preventDefault();

            if (event.target.classList.contains(CLASS_PREVENT_SUBMISSION)) {
              return;
            }
            // Need to protect against null when submitted with Enter
            if (submitEl) {
              submitEl.disabled = true;
              submitEl.classList.add(CLASS_BUSY);
            }
            validator
              .validate()
              .then(status => {
                if (status !== 'Valid') {
                  return renderGlobalError(slideContainer, {
                    focusFirstError: true
                  });
                } else {
                  // Prevent JS handling if a target is set on the form
                  if (event.target.hasAttribute('target')) {
                    event.target.submit();
                    return;
                  } else {
                    return navigate(() => submit(event.target, submitEl));
                  }
                }
              })
              .finally(() => {
                if (submitEl) {
                  submitEl.disabled = false;
                  submitEl.classList.remove(CLASS_BUSY);
                }
              });
          }
        })(event);
      }

      function handleLinkNavigation(event) {
        return delegate('.js-form-link', (event, currentTarget) => {
          if (
            !event.defaultPrevented &&
            !hasModifiers(event) &&
            !isHashLinkOnSamePage(currentTarget) &&
            !isEmptyHashLink(currentTarget)
          ) {
            event.preventDefault();
            currentTarget.classList.add(CLASS_BUSY);
            navigate(() =>
              visit(currentTarget.href, {
                data: withinIframe ? { layout_type: 'iframe' } : null
              })
            ).finally(() => {
              currentTarget.classList.remove(CLASS_BUSY);
            });
          }
        })(event);
      }
      let pendingActions = 0;
      function navigate(navigationAction, { pushState = true } = {}) {
        const slide = slideContainer.querySelector(SELECTOR_SLIDE);
        slide.setAttribute('aria-busy', true);
        slide.setAttribute('inert', '');
        // Little counter to account for users spamming the back button
        pendingActions++;
        scrollToStart();
        return navigationAction()
          .then(intents =>
            handleIntents(slide, intents, {
              setup,
              pushState,
              slideEl: el,
              slideIndex: index
            })
          )
          .catch(e => {
            if (e.type !== 'abort') {
              renderGlobalError(slideContainer, {
                message: ERROR_MESSAGE
              });
            }
            scrollToStart();
          })
          .finally(() => {
            pendingActions--;
            // Just a little safety if the pending actions get in the negative range
            // so they do start back from  0
            if (pendingActions < 1) {
              pendingActions = 0;
              slide.removeAttribute('aria-busy');
              slide.removeAttribute('inert');
            }
          });
      }
    });
  });
}

function addAnchor(scope) {
  if (!scope.querySelector(SELECTOR_ANCHOR)) {
    // Inject an extra anchor to control the spacing
    const anchor = document.createElement('span');
    anchor.classList.add(CLASS_ANCHOR);
    scope.appendChild(anchor);
  }
}

function isWithinIframe(el) {
  const withinIframe = el.querySelector('[name="layout_type"]');
  return withinIframe && withinIframe.value == 'iframe';
}



// WEBPACK FOOTER //
// ./app/webpack/packs/embed.js